import { useQuery, useQueryClient } from "@tanstack/react-query";
import { coursesService } from "@/services/coursesService";
import { recipesService } from "@/services/recipesService";
import { libraryBookmarksService } from "@/services/libraryBookmarksService";
import { challengeService } from "@/services/challengeService";
import { userChallengeService } from "@/services/userChallengeService";
import { courseAccessService } from "@/services/courseAccessService";
import { supabasePostsService } from "@/services/supabasePostsService";
import { useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import { preloadMainPages } from "@/components/LazyPages";

const LIBRARY_KEYS = {
  courses: ["library", "courses"] as const,
  coursesProgress: ["library", "courses", "progress"] as const,
  coursesAccess: ["library", "courses", "access"] as const,
  recipes: ["library", "recipes"] as const,
  recipeBookmarks: ["library", "recipes", "bookmarks"] as const,
  challenges: ["library", "challenges"] as const,
  challengeEnrollments: ["library", "challenges", "enrollments"] as const,
  libraryBookmarks: ["library", "bookmarks"] as const,
  communityBookmarks: ["library", "community-bookmarks"] as const,
  communityPosts: ["community", "posts", "latest"] as const,
};

// Courses Query
export function useCoursesQuery() {
  return useQuery({
    queryKey: LIBRARY_KEYS.courses,
    queryFn: () => coursesService.getAllPublishedCourses(),
    staleTime: 5 * 60 * 1000,
  });
}

export function useCoursesProgressQuery() {
  const { effectiveUserId } = useImpersonation();
  return useQuery({
    queryKey: [...LIBRARY_KEYS.coursesProgress, effectiveUserId],
    queryFn: () => coursesService.getAllUserProgress(effectiveUserId),
    staleTime: 5 * 60 * 1000,
    enabled: !!effectiveUserId,
  });
}

export function useCoursesAccessQuery() {
  const { effectiveUserId } = useImpersonation();
  return useQuery({
    queryKey: [...LIBRARY_KEYS.coursesAccess, effectiveUserId],
    queryFn: () => courseAccessService.getAccessibleCourseIds(effectiveUserId),
    staleTime: 5 * 60 * 1000,
    enabled: !!effectiveUserId,
  });
}

// Recipes Query
export function useRecipesQuery() {
  return useQuery({
    queryKey: LIBRARY_KEYS.recipes,
    queryFn: () => recipesService.getAllPublishedRecipes(),
    staleTime: 5 * 60 * 1000,
  });
}

export function useRecipeBookmarksQuery() {
  const { effectiveUserId } = useImpersonation();
  return useQuery({
    queryKey: [...LIBRARY_KEYS.recipeBookmarks, effectiveUserId],
    queryFn: () => libraryBookmarksService.getBookmarks("recipe", effectiveUserId),
    staleTime: 5 * 60 * 1000,
    enabled: !!effectiveUserId,
  });
}

// Challenges Query
export function useChallengesQuery() {
  return useQuery({
    queryKey: LIBRARY_KEYS.challenges,
    queryFn: () => challengeService.getAllChallenges(),
    staleTime: 5 * 60 * 1000,
  });
}

export function useChallengeEnrollmentsQuery() {
  const { isLoading: authLoading } = useAuth();
  const { effectiveUserId } = useImpersonation();
  return useQuery({
    queryKey: [...LIBRARY_KEYS.challengeEnrollments, effectiveUserId],
    queryFn: () => userChallengeService.getUserEnrollments(effectiveUserId),
    staleTime: 5 * 60 * 1000,
    enabled: !!effectiveUserId && !authLoading,
  });
}

// Bookmarks Query
export function useLibraryBookmarksQuery() {
  const { effectiveUserId } = useImpersonation();
  return useQuery({
    queryKey: [...LIBRARY_KEYS.libraryBookmarks, effectiveUserId],
    queryFn: () => libraryBookmarksService.getBookmarks(undefined, effectiveUserId),
    staleTime: 5 * 60 * 1000,
    enabled: !!effectiveUserId,
  });
}

export function useCommunityBookmarksQuery() {
  const { effectiveUserId } = useImpersonation();
  return useQuery({
    queryKey: [...LIBRARY_KEYS.communityBookmarks, effectiveUserId],
    queryFn: async () => {
      if (!effectiveUserId) return { posts: [], comments: [] };

      const [posts, comments] = await Promise.all([
        supabasePostsService.getBookmarks(effectiveUserId),
        supabasePostsService.getBookmarkedComments(effectiveUserId),
      ]);
      return { posts, comments };
    },
    staleTime: 5 * 60 * 1000,
    enabled: !!effectiveUserId,
  });
}

// Community Posts Query
export function useCommunityPostsQuery() {
  return useQuery({
    queryKey: LIBRARY_KEYS.communityPosts,
    queryFn: () => supabasePostsService.getAllPosts('latest', undefined, 20, 0),
    staleTime: 2 * 60 * 1000,
  });
}

// Prefetch Hook - טעינה מקדימה ברקע, event-driven
export function useLibraryPrefetch({ enabled = true } = {}) {
  const queryClient = useQueryClient();
  const { isLoading } = useAuth();
  const { effectiveUserId } = useImpersonation();

  useEffect(() => {
    if (!effectiveUserId || isLoading || !enabled) return;

    const prefetchAll = async () => {
      await Promise.all([
        queryClient.prefetchQuery({
          queryKey: LIBRARY_KEYS.courses,
          queryFn: () => coursesService.getAllPublishedCourses(),
          staleTime: 5 * 60 * 1000,
        }),
        queryClient.prefetchQuery({
          queryKey: [...LIBRARY_KEYS.coursesProgress, effectiveUserId],
          queryFn: () => coursesService.getAllUserProgress(effectiveUserId),
          staleTime: 5 * 60 * 1000,
        }),
        queryClient.prefetchQuery({
          queryKey: [...LIBRARY_KEYS.coursesAccess, effectiveUserId],
          queryFn: () => courseAccessService.getAccessibleCourseIds(effectiveUserId),
          staleTime: 5 * 60 * 1000,
        }),
        
        queryClient.prefetchQuery({
          queryKey: LIBRARY_KEYS.recipes,
          queryFn: () => recipesService.getAllPublishedRecipes(),
          staleTime: 5 * 60 * 1000,
        }),
        queryClient.prefetchQuery({
          queryKey: [...LIBRARY_KEYS.recipeBookmarks, effectiveUserId],
          queryFn: () => libraryBookmarksService.getBookmarks("recipe", effectiveUserId),
          staleTime: 5 * 60 * 1000,
        }),
        
        queryClient.prefetchQuery({
          queryKey: LIBRARY_KEYS.challenges,
          queryFn: () => challengeService.getAllChallenges(),
          staleTime: 5 * 60 * 1000,
        }),
        queryClient.prefetchQuery({
          queryKey: [...LIBRARY_KEYS.challengeEnrollments, effectiveUserId],
          queryFn: () => userChallengeService.getUserEnrollments(effectiveUserId),
          staleTime: 5 * 60 * 1000,
        }),
        
        queryClient.prefetchQuery({
          queryKey: [...LIBRARY_KEYS.libraryBookmarks, effectiveUserId],
          queryFn: () => libraryBookmarksService.getBookmarks(undefined, effectiveUserId),
          staleTime: 5 * 60 * 1000,
        }),
        queryClient.prefetchQuery({
          queryKey: [...LIBRARY_KEYS.communityBookmarks, effectiveUserId],
          queryFn: async () => {
            if (!effectiveUserId) return { posts: [], comments: [] };

            const [posts, comments] = await Promise.all([
              supabasePostsService.getBookmarks(effectiveUserId),
              supabasePostsService.getBookmarkedComments(effectiveUserId),
            ]);
            return { posts, comments };
          },
          staleTime: 5 * 60 * 1000,
        }),
        
        queryClient.prefetchQuery({
          queryKey: LIBRARY_KEYS.communityPosts,
          queryFn: () => supabasePostsService.getAllPosts('latest', undefined, 20, 0),
          staleTime: 2 * 60 * 1000,
        }),
      ]);

      // אחרי שכל ה-prefetch הסתיים -- preload JS bundles של דפים ראשיים
      preloadMainPages();
    };

    prefetchAll().catch(console.error);
  }, [effectiveUserId, isLoading, enabled, queryClient]);
}
